/**
 * FilterMenu
 * Classification: custom (composes Chip + Popover + Search + Checkbox list)
 *
 * One filter field behind a chip: the chip names the field and its selection,
 * the panel is a list of checkboxes. This is the third member of the filter
 * family and the smallest -- `FilterPanel` is an always-visible sidebar,
 * `FilterPopover` puts several fields behind one button, and this puts one field
 * behind one chip, so a filter bar can show what is applied and let the user
 * change it in the same place.
 *
 * The surface is a Popover holding real `Checkbox` inputs, not a Menu of
 * `menuitemcheckbox` rows. A menu's role cannot legally contain the search
 * field or the Apply button this panel needs, and native checkboxes come with
 * the announcement and keyboard behaviour that a menu would have to reimplement.
 */
import { useId, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@/atoms/Button';
import { Chip, type ChipSize, type PickerSummary } from '@/atoms/Chip';
import { Search } from '@/molecules/Search';
import { backgroundColors, scrim } from '@/tokens/colors';
import type { FilterOption } from './types';

export interface FilterMenuProps {
  /** The field's name, e.g. `Severity`. Shown on the chip and names the panel. */
  field: string;
  options: FilterOption[];
  /** Controlled selection. Omit for uncontrolled. */
  value?: string[];
  defaultValue?: string[];
  /**
   * Called with the new selection. Under `commit="immediate"` this fires on
   * every toggle; under `"apply"` only when the user presses Apply.
   */
  onChange: (values: string[]) => void;
  /**
   * When the selection takes effect.
   *
   * `immediate` is the default because a chip is a cheap, reversible control
   * and live results are the faster loop. Use `apply` when each change costs a
   * query the user should not pay for mid-thought, or when partial selections
   * would show a misleading result.
   */
  commit?: 'immediate' | 'apply';
  /** Adds a filter field above the options. Worth it past about ten options. */
  searchable?: boolean;
  /** Adds an `All` row that toggles everything currently visible. */
  showSelectAll?: boolean;
  /** How the chip summarises the selection. `count` for values too long to show. */
  summary?: PickerSummary;
  /**
   * Removes the whole filter, which is what the X on the chip means -- the same
   * thing it means on `Chip purpose="filter"`. Drop the field from your list of
   * active filters here; this component cannot do it, since the list is yours.
   *
   * Omit it for a bar of fixed fields. Pass it only if the user has some way to
   * add the filter back, such as an `Add filter` menu, or removing it is a dead
   * end.
   *
   * Emptying the selection while keeping the chip is the panel's `Reset`, and
   * needs no prop.
   */
  onRemove?: () => void;
  /**
   * Fires when the panel opens. For lazily loading options, or analytics.
   */
  onOpen?: () => void;
  /**
   * Fires when the panel closes, with the selection as applied at that moment
   * -- so under `commit="apply"` a panel dismissed without Apply reports the
   * old values, not the abandoned draft.
   *
   * This is the hook for running one query per visit rather than one per tick:
   * keep `commit="immediate"` so the checkboxes and the chip stay live, drive
   * local state from `onChange`, and refetch here.
   */
  onClose?: (values: string[]) => void;
  loading?: boolean;
  /** A leading icon on the chip, e.g. the kind of thing being filtered. */
  icon?: React.ReactElement;
  /** Shown when a search matches nothing. */
  emptyText?: string;
  /**
   * Size of the picker trigger chip. Panel internals stay small.
   * @default 'medium'
   */
  size?: ChipSize;
}

/** Options in the order given, grouped under their headings. */
interface OptionGroup {
  heading?: string;
  options: FilterOption[];
}

/**
 * @example
 * <FilterMenu
 *   field="Severity"
 *   options={[{ label: 'Critical', value: 'critical' }, { label: 'High', value: 'high' }]}
 *   value={severities}
 *   onChange={setSeverities}
 *   onRemove={() => dropFilter('severity')}
 * />
 */
export function FilterMenu({
  field,
  options,
  value,
  defaultValue,
  onChange,
  commit = 'immediate',
  searchable = false,
  showSelectAll = false,
  summary = 'value',
  onRemove,
  onOpen,
  onClose,
  loading = false,
  icon,
  emptyText = 'No matches',
  size = 'medium',
}: FilterMenuProps) {
  const isControlled = value !== undefined;
  const isApply = commit === 'apply';

  const [internal, setInternal] = useState<string[]>(defaultValue ?? []);
  const applied = isControlled ? value : internal;

  const [draft, setDraft] = useState<string[]>(applied);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const panelId = useId();

  const open = Boolean(anchorEl);

  /**
   * What the checkboxes reflect. Under `apply` that is the uncommitted draft,
   * which is also why the chip keeps showing `applied` while the panel is open:
   * the two are genuinely different states and conflating them would tell the
   * user a filter is active before it is.
   */
  const working = isApply ? draft : applied;

  /**
   * Selections are held in the order the options were declared, not the order
   * they were clicked. The chip shows the first one, so click order would make
   * the same two severities read as `Critical +1` or `High +1` depending on
   * which was ticked first. Values no longer in `options` sort last rather than
   * being dropped, so a stale saved filter is visible instead of silently gone.
   */
  const order = useMemo(
    () => new Map(options.map((option, index) => [option.value, index])),
    [options],
  );
  const inOptionOrder = (values: string[]) =>
    [...values].sort(
      (a, b) =>
        (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER),
    );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query]);

  /** A Map keeps first-appearance order, so interleaved groups still merge. */
  const groups = useMemo<OptionGroup[]>(() => {
    const byHeading = new Map<string | undefined, FilterOption[]>();
    for (const option of visible) {
      const existing = byHeading.get(option.group);
      if (existing) existing.push(option);
      else byHeading.set(option.group, [option]);
    }
    return [...byHeading].map(([heading, opts]) => ({ heading, options: opts }));
  }, [visible]);

  const emit = (next: string[]) => {
    if (!isControlled) setInternal(next);
    onChange(next);
  };

  /** Under `apply` a change is a draft edit; otherwise it is the change itself. */
  const put = (next: string[]) => (isApply ? setDraft(next) : emit(next));

  const openPanel = (event: React.MouseEvent<HTMLElement>) => {
    // Re-seed the draft on every open so a panel abandoned without Apply does
    // not reopen holding the changes the user walked away from.
    setDraft(applied);
    setQuery('');
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };

  /**
   * `committed` lets `apply` report the values it just emitted rather than the
   * `applied` of this render, which React has not updated yet -- otherwise
   * Apply-then-close would announce the previous selection.
   */
  const closePanel = (committed?: string[]) => {
    setAnchorEl(null);
    onClose?.(committed ?? applied);
  };

  const toggle = (optionValue: string) => {
    put(
      inOptionOrder(
        working.includes(optionValue)
          ? working.filter((v) => v !== optionValue)
          : [...working, optionValue],
      ),
    );
  };

  // Select-all acts on what is on screen, not on every option. With a search
  // active, "All" meaning "all 400, including the 397 you filtered out" is a
  // trap; meaning "these three" is what the user sees and expects.
  const allVisibleSelected =
    visible.length > 0 && visible.every((option) => working.includes(option.value));
  const someVisibleSelected =
    !allVisibleSelected && visible.some((option) => working.includes(option.value));

  const toggleAllVisible = () => {
    put(
      inOptionOrder(
        allVisibleSelected
          ? working.filter((v) => !visible.some((option) => option.value === v))
          : [...new Set([...working, ...visible.map((option) => option.value)])],
      ),
    );
  };

  const reset = () => put([]);

  const apply = () => {
    emit(draft);
    closePanel(draft);
  };

  const labelFor = (optionValue: string) =>
    options.find((option) => option.value === optionValue)?.label ?? optionValue;

  return (
    <>
      <Chip
        purpose="picker"
        field={field}
        // The chip speaks labels, never raw option values.
        values={inOptionOrder(applied).map(labelFor)}
        summary={summary}
        expanded={open}
        panelId={panelId}
        onOpen={openPanel}
        onRemove={onRemove}
        icon={icon}
        size={size}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => closePanel()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            id: panelId,
            // Named so a user who opened one of several chips in a filter bar
            // knows which panel they are in.
            role: 'dialog',
            'aria-label': `${field} filter`,
            // Match EntityMenu / MuiMenu: `surface` in dark, paper (white) in light.
            sx: (theme) => ({
              width: 300,
              mt: 0.5,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? backgroundColors.surface
                  : theme.palette.background.paper,
              backgroundImage: 'none',
            }),
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `rgba(0, 0, 0, ${scrim.loading})`,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {/* The header names the panel and holds Reset. It renders whether or
              not Reset is showing, so ticking the first option does not shove
              the list down by a row. */}
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 1 }}
          >
            <Typography variant="subtitle2">{field}</Typography>
            {working.length > 0 && (
              <Button variant="ghost" size="small" onClick={reset}>
                Reset
              </Button>
            )}
          </Stack>

          <Box sx={{ px: 2, pb: 1, maxHeight: 320, overflowY: 'auto' }}>
            {searchable && (
              <Box sx={{ pb: 1 }}>
                <Search
                  size="small"
                  fullWidth
                  autoFocus
                  placeholder={`Filter ${field.toLowerCase()}...`}
                  onChange={setQuery}
                  aria-label={`Filter ${field} options`}
                />
              </Box>
            )}

            {showSelectAll && visible.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleAllVisible}
                  />
                }
                label={<Typography variant="body1">All</Typography>}
              />
            )}

            {visible.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                {emptyText}
              </Typography>
            )}

            {groups.map((group) => (
              <Box key={group.heading ?? '__ungrouped__'}>
                {group.heading && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      pt: 1,
                    }}
                  >
                    {group.heading}
                  </Typography>
                )}
                <Stack>
                  {group.options.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={working.includes(option.value)}
                          onChange={() => toggle(option.value)}
                        />
                      }
                      label={<Typography variant="body1">{option.label}</Typography>}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>

          {isApply && (
            <>
              <Divider />
              <Stack direction="row" sx={{ justifyContent: 'flex-end', p: 1.5 }}>
                <Button variant="primary" size="small" onClick={apply} disabled={loading}>
                  Apply
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default FilterMenu;
